/**
 * Anonymous usage statistics and error reports (Cloudflare build only: POST /api/t, D1).
 * - feature events without personal data (map picks rounded to ~1 km), Web Vitals, JS / API errors;
 * - no cookies, no stored identifiers: a random id per tab, the server counts visitors with a
 *   daily rotating hash; batched with navigator.sendBeacon;
 * - "Do Not Track" / Global Privacy Control, or the switch in Help > Nguồn dữ liệu: errors only.
 */
import { V } from './assetv.js';

const OPT_KEY = 'qh.telemetry';
const EDGE = typeof document !== 'undefined' && document.querySelector('meta[name="qh-backend"]')?.content === 'edge';
const queue = [];
const errs = [];
const seenErr = new Set();
let timer = 0;
let sent = 0;
let started = false;
const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
const sid = Math.random().toString(36).slice(2, 12).padEnd(8, '0');

const privacySignal = () => navigator.doNotTrack === '1' || window.doNotTrack === '1' || navigator.globalPrivacyControl === true;
let optedOut = (() => { try { return localStorage.getItem(OPT_KEY) === 'off'; } catch { return false; } })();

/** Usage statistics enabled? (errors are reported unless the user opted out) */
export const statsEnabled = () => EDGE && !optedOut && !privacySignal();
export const statsBlockedBySignal = () => privacySignal();
export const isOptedOut = () => optedOut;
export function setOptOut(off) {
  optedOut = !!off;
  try { if (off) localStorage.setItem(OPT_KEY, 'off'); else localStorage.removeItem(OPT_KEY); } catch { /* private mode */ }
  if (off) { queue.length = 0; errs.length = 0; }
}

/** Record a usage event: track('act', 'layer', '654'). */
export function track(type, name, value = null, num = null) {
  if (!statsEnabled() || sent + queue.length > 400) return;
  queue.push([type, String(name).slice(0, 48), value == null ? null : String(value).slice(0, 64), Number.isFinite(num) ? num : null, Math.round(performance.now() - t0)]);
  schedule();
}
export const act = (name, value) => track('act', name, value);
/** ~1 km grid cell of a location (enough for "which areas are looked up", not who). */
export const area = (lat, lng) => `${(+lat).toFixed(2)},${(+lng).toFixed(2)}`;

/** Report an error (deduplicated per page). */
export function reportError(kind, message, { src = '', line = 0, col = 0, stack = '' } = {}) {
  if (!EDGE || optedOut) return;
  const m = String(message || '').slice(0, 300);
  const key = `${kind}|${m}|${src}:${line}`;
  if (seenErr.has(key) || seenErr.size > 20) return;
  seenErr.add(key);
  errs.push({ k: kind, m, s: String(src).slice(0, 160), l: line | 0, c: col | 0, st: String(stack || '').slice(0, 1200), u: location.pathname });
  schedule(1500);
}

function schedule(ms = 8000) {
  if (queue.length >= 25) { flush(); return; }
  if (!timer) timer = setTimeout(flush, ms);
}

function flush() {
  clearTimeout(timer);
  timer = 0;
  if (!queue.length && !errs.length) return;
  const body = { sid, v: V.build || 'dev', ev: queue.splice(0, 60), err: errs.splice(0, 8) };
  if (!sent) { const r = refHost(); if (r) body.ref = r; }
  sent += body.ev.length;
  const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
  try {
    if (!(navigator.sendBeacon && navigator.sendBeacon('/api/t', blob))) fetch('/api/t', { method: 'POST', body: blob, keepalive: true }).catch(() => {});
  } catch { /* never let statistics break the app */ }
  if (queue.length || errs.length) schedule(2000);
}

function refHost() {
  try {
    const h = document.referrer ? new URL(document.referrer).hostname : '';
    return h && h !== location.hostname ? h.replace(/^www\./, '').replace(/^(l|lm|m)\.facebook\.com$/, 'facebook.com') : '';
  } catch { return ''; }
}

/* ---------------- Web Vitals (LCP, CLS, INP, FCP, TTFB) ---------------- */

function vitals() {
  const v = { lcp: 0, cls: 0, inp: 0 };
  let clsWin = 0, clsLast = 0, clsFirst = 0;
  const durations = new Map();
  const observe = (type, fn, opts = {}) => {
    try { new PerformanceObserver((l) => l.getEntries().forEach(fn)).observe({ type, buffered: true, ...opts }); } catch { /* unsupported */ }
  };
  observe('largest-contentful-paint', (e) => { v.lcp = e.startTime; });
  observe('layout-shift', (e) => {
    if (e.hadRecentInput) return;
    // session windows: shifts < 1 s apart, window ≤ 5 s; CLS = the largest window
    if (clsWin && e.startTime - clsLast < 1000 && e.startTime - clsFirst < 5000) clsWin += e.value;
    else { clsWin = e.value; clsFirst = e.startTime; }
    clsLast = e.startTime;
    v.cls = Math.max(v.cls, clsWin);
  });
  observe('event', (e) => {
    if (!e.interactionId) return;
    durations.set(e.interactionId, Math.max(durations.get(e.interactionId) || 0, e.duration));
  }, { durationThreshold: 40 });
  observe('paint', (e) => { if (e.name === 'first-contentful-paint') track('vital', 'fcp', null, Math.round(e.startTime)); });
  const nav = performance.getEntriesByType?.('navigation')?.[0];
  if (nav) track('vital', 'ttfb', null, Math.round(nav.responseStart));
  let done = false;
  const final = () => {
    if (done) return;
    done = true;
    if (v.lcp) track('vital', 'lcp', null, Math.round(v.lcp));
    track('vital', 'cls', null, Math.round(v.cls * 1000) / 1000);
    if (durations.size) {
      // INP ≈ the worst interaction, ignoring one outlier per 50 interactions
      const d = [...durations.values()].sort((a, b) => b - a);
      track('vital', 'inp', null, Math.round(d[Math.min(d.length - 1, Math.floor(d.length / 50))]));
    }
  };
  addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') { final(); flush(); } });
  addEventListener('pagehide', () => { final(); flush(); });
}

/** Start: page view, vitals, global error handlers. */
export function initTelemetry({ entry = 'direct' } = {}) {
  if (started || !EDGE) return;
  started = true;
  addEventListener('error', (e) => {
    if (e.target && e.target !== window && e.target.tagName) return; // resource errors: not ours to report one by one
    reportError('js', e.message, { src: (e.filename || '').replace(location.origin, ''), line: e.lineno, col: e.colno, stack: e.error?.stack });
  });
  addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    if (r && (r.name === 'AbortError' || r.name === 'TimeoutError')) return;
    reportError('promise', r?.message || String(r), { stack: r?.stack });
  });
  if (!statsEnabled()) return;
  track('pv', 'view', entry, null);
  if (matchMedia('(display-mode: standalone)').matches) act('standalone');
  vitals();
  // first batch soon: short visits (closed within a few seconds, pagehide not always fired) still count
  clearTimeout(timer);
  timer = setTimeout(flush, 1500);
}
